import {
  Box,
  Card,
  Dialog,
  Stack,
  TextField as MuiTextField,
  MenuItem,
  Grid,
  Chip,
  Button,
  TablePagination,
} from '@mui/material';
import { ReactElement, useState, useEffect, useCallback } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  TextField,
  useTranslate,
  DeleteButton,
  FunctionField,
  useNotify,
} from 'react-admin';

import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getDesignatedRedPackets,
  deleteDesignatedRedPacket,
  DesignatedRedPacket,
  DesignatedRedPacketFilter,
  DesignatedRedPacketStatus,
  PaginationInput,
} from '../../../services/redpacketService';
import CreateDesignatedRedPacket from './Create';
import CreateBatchDesignatedRedPacket from './CreateBatch';
import EditDesignatedRedPacket from './Edit';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';

const DesignatedRedPacketsList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();
  const [data, setData] = useState<DesignatedRedPacket[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  const [filter, setFilter] = useState<DesignatedRedPacketFilter>({});
  const [filterValues, setFilterValues] = useState({
    walletAddress: '',
    status: '' as DesignatedRedPacketStatus | '',
    createdFrom: null as Dayjs | null,
    createdTo: null as Dayjs | null,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (pagination.page - 1) * pagination.perPage;
      const paginationInput: PaginationInput = {
        limit: pagination.perPage,
        offset,
      };
      const result = await getDesignatedRedPackets(filter, paginationInput);
      setData(Array.isArray(result.records) ? result.records : []);
      setTotal(result.totalCount || 0);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, filter, notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await deleteDesignatedRedPacket(id);
        notify('Delete successfully', { type: 'success' });
        fetchData();
      } catch (error: any) {
        notify(error.message || 'Delete failed', { type: 'error' });
      }
    }
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    const newFilter: DesignatedRedPacketFilter = {
      walletAddress: filterValues.walletAddress || undefined,
      status: filterValues.status || undefined,
      createdFrom: filterValues.createdFrom
        ? filterValues.createdFrom.toISOString()
        : undefined,
      createdTo: filterValues.createdTo ? filterValues.createdTo.toISOString(): undefined,
    };
    setFilter(newFilter);
    setPagination({ page: 1, perPage: pagination.perPage });
  };

  const getStatusColor = (status: DesignatedRedPacketStatus) => {
    switch (status) {
      case DesignatedRedPacketStatus.PENDING:
        return 'warning';
      case DesignatedRedPacketStatus.TRIGGERED:
        return 'success';
      case DesignatedRedPacketStatus.EXPIRED:
        return 'error';
      default:
        return 'default';
    }
  };
  const handlePageChange = (_: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({ ...prev, perPage: parseInt(e.target.value, 10), page: 0 }));
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.designatedRedPackets')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.designatedRedPackets') },
          ]}
          action={
            <PermissionGuard resource="redpacket" action="create" fallback={<></>}>
              <Stack direction="row" spacing={2}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(
                      <CreateDesignatedRedPacket
                        onClose={() => {
                          setPopup(undefined);
                          fetchData();
                        }}
                      />
                    );
                  }}
                />
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(
                      <CreateBatchDesignatedRedPacket
                        onClose={() => {
                          setPopup(undefined);
                          fetchData();
                        }}
                      />
                    );
                  }}
                >
                  CreateBatch
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => {
                    const csv = [
                      // Header row
                      ['ID', 'Wallet Address', 'Designated Amount', 'Status', 'Triggered At', 'Triggered By', 'Red Packet ID', 'Created By', 'Expires At', 'Created At', 'Updated At'],
                      // Data rows
                      ...data.map(row =>  [
                        row.id,
                        row.walletAddress,
                        row.designatedAmount,
                        row.status,
                        row.triggeredAt ? new Date(row.triggeredAt).toLocaleString() : '',
                        row.triggeredBy ?? '',
                        row.redPacketId ?? '',
                        row.createdBy ?? '',
                        row.expiresAt ? new Date(row.expiresAt).toLocaleString() : '',
                        new Date(row.createdAt).toLocaleString(),
                        new Date(row.updatedAt).toLocaleString(),
                      ]),
                    ]
                      .map(row =>
                        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
                      )
                      .join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'designated-redpackets.csv';
                    a.click();
                    URL.revokeObjectURL(url); // giải phóng bộ nhớ sau khi download
                  }}
                >
                  Export CSV
                </Button>
              </Stack>
            </PermissionGuard>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          {/* 自定义筛选表单 */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MuiTextField
                    fullWidth
                    label={translate('module.redpacket.walletAddress')}
                    value={filterValues.walletAddress}
                    onChange={(e) => handleFilterChange('walletAddress', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <MuiTextField
                    fullWidth
                    select
                    label={translate('module.redpacket.status')}
                    value={filterValues.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    size="small"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={DesignatedRedPacketStatus.PENDING}>Pending</MenuItem>
                    <MenuItem value={DesignatedRedPacketStatus.TRIGGERED}>Triggered</MenuItem>
                    <MenuItem value={DesignatedRedPacketStatus.EXPIRED}>Expired</MenuItem>
                    <MenuItem value={DesignatedRedPacketStatus.CANCELLED}>Cancelled</MenuItem>
                  </MuiTextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label={translate('module.redpacket.createdFrom')}
                    value={filterValues.createdFrom}
                    onChange={(value) => handleFilterChange('createdFrom', value)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label={translate('module.redpacket.createdTo')}
                    value={filterValues.createdTo}
                    onChange={(value) => handleFilterChange('createdTo', value)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={2}>
                    <Button variant="contained" onClick={handleApplyFilter}>
                      {translate('module.redpacket.search')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilterValues({
                          walletAddress: '',
                          status: '',
                          createdFrom: null,
                          createdTo: null,
                        });
                        setFilter({});
                      }}
                    >
                      {translate('module.redpacket.reset')}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>

          <Box sx={{ overflow: 'auto' }}>
            <Datagrid
              rowClick={false}
              data={data}
              isLoading={loading}
              total={total}
              bulkActionButtons={false}
            >
              <TextField source="walletAddress" label={translate('module.redpacket.walletAddress')} />
              <TextField source="designatedAmount" label={translate('module.redpacket.designatedAmount')} />
              <FunctionField
                label={translate('module.redpacket.status')}
                render={(record: DesignatedRedPacket) => (
                  <Chip
                    label={record.status}
                    color={getStatusColor(record.status) as any}
                    size="small"
                  />
                )}
              />
              <FunctionField
                label={translate('module.redpacket.triggeredAt')}
                render={(record: DesignatedRedPacket) => {
                  const date = record.triggeredAt;
                  return date ? new Date(date).toLocaleString() : '-';
                }}
              />
              <TextField source="triggeredBy" label={translate('module.redpacket.triggeredBy')} />
              <TextField source="createdBy" label={translate('module.redpacket.createdBy')} />
              <FunctionField
                label={translate('module.redpacket.expiresAt')}
                render={(record: DesignatedRedPacket) => {
                  const date = record.expiresAt;
                  return date ? new Date(date).toLocaleString() : '-';
                }}
              />
              <FunctionField
                label={translate('module.redpacket.createdAtCol')}
                render={(record: DesignatedRedPacket) => {
                  const date = record.createdAt;
                  return date ? new Date(date).toLocaleString() : '-';
                }}
              />

              <FunctionField
                label={translate('module.redpacket.operation')}
                render={(record: DesignatedRedPacket) => (
                  <Stack direction="row" spacing={1}>
                    {record.status === DesignatedRedPacketStatus.PENDING && (
                      <PermissionButton resource="redpacket" action="update">
                        <EditButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setPopup(
                              <EditDesignatedRedPacket
                                id={record.id}
                                record={record}
                                onClose={() => {
                                  setPopup(undefined);
                                  fetchData();
                                }}
                              />
                            );
                          }}
                        />
                      </PermissionButton>
                    )}
                    {record.status === DesignatedRedPacketStatus.PENDING && (
                      <PermissionButton resource="redpacket" action="delete">
                        <DeleteButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(record.id);
                          }}
                        />
                      </PermissionButton>
                    )}
                  </Stack>
                )}
              />
            </Datagrid>
          </Box>
        </Card>

        {popup && (
          <Dialog open maxWidth="md" fullWidth>
            {popup}
          </Dialog>
        )}
      </DashboardContent>
      <TablePagination
        component="div"
        count={total}
        page={pagination.page}
        onPageChange={handlePageChange}
        rowsPerPage={pagination.perPage}
        onRowsPerPageChange={handlePerPageChange}
        rowsPerPageOptions={[10, 20, 50]}
      />
    </PermissionGuard>
  );
};

export default DesignatedRedPacketsList;
